"use client";

import { useLingui } from "@lingui/react/macro";
import { AccessibleIcon } from "@offstreetparking/design-system/accessibility";
import { Divider } from "@offstreetparking/design-system/dataDisplay/Divider";
import { Button } from "@offstreetparking/design-system/forms";
import { ResetIcon } from "@offstreetparking/design-system/icons/ResetIcon";
import { Text } from "@offstreetparking/design-system/typography";
import { useState } from "react";
import { routes } from "../../../../routes/routes";
import { ResponsiveOverlay } from "../../../overlays/ResponsiveOverlay";
import { useKiosk } from "../../../providers/KioskProvider";

type KioskStartOverProps = {
	isDisabled?: boolean;
};

export const KioskStartOver = ({ isDisabled }: KioskStartOverProps) => {
	const { t } = useLingui();
	const { startOverLookupId } = useKiosk();
	const [isConfirming, setIsConfirming] = useState(false);

	if (!startOverLookupId) {
		return null;
	}

	const startOver = () => {
		window.location.href = routes.locationLookup({ lookup: startOverLookupId });
	};

	const startOverLabel = t({
		comment: "Action that discards the in-progress registration and returns the parker to the start",
		message: "Start Over",
	});

	return (
		<>
			<Button
				intent="secondary"
				type="button"
				leftIcon={ResetIcon}
				size="lg"
				className="w-full md:w-auto"
				isDisabled={isDisabled}
				onClick={() => setIsConfirming(true)}
			>
				{startOverLabel}
			</Button>

			<ResponsiveOverlay
				isOpen={isConfirming}
				onOpenChange={setIsConfirming}
				title={
					<div className="flex items-center gap-3">
						<div className="flex h-8 w-8 items-center justify-center rounded-full bg-neutral-faded">
							<AccessibleIcon>
								<ResetIcon className="h-4 w-4 text-icon-neutral" />
							</AccessibleIcon>
						</div>
						<Text weight="bold" size="lg">
							{t({
								comment:
									"Title of the dialog confirming the parker wants to abandon their in-progress kiosk registration",
								message: "Start Over?",
							})}
						</Text>
					</div>
				}
			>
				<Divider hideOnMobile={false} />

				<div className="mt-4 flex flex-col gap-4">
					<Text>
						{t({
							comment:
								"Body of the kiosk start-over dialog, warning that the half-filled registration form will be cleared",
							message: "Any information you've entered will be lost.",
						})}
					</Text>
					<div className="flex flex-col gap-3">
						<Button intent="primary" type="button" className="w-full" size="md" onClick={startOver}>
							{startOverLabel}
						</Button>
						<Button
							intent="secondary"
							type="button"
							className="w-full"
							size="md"
							onClick={() => setIsConfirming(false)}
						>
							{t({
								comment: "Dismisses the kiosk start-over dialog and keeps the registration in progress",
								message: "Cancel",
							})}
						</Button>
					</div>
				</div>
			</ResponsiveOverlay>
		</>
	);
};
